import createComponent from '@carvana/merch-components/createComponent';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { useCallback, useContext, useMemo, useState } from 'react';
import TrackWrapper from '@/common/TrackWrapper';
import { PACKAGES_SECTION_ID } from '~/constants/new-cars/sectionIds';
import { SECTION_VIEW_THRESHOLD } from '~/constants/new-cars/sectionViewThreshold';
import { useModalContext } from '~/context/new-cars/ModalContext';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type { CustomizePackageOption } from '~/contracts/new-cars';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import PackageOptionCard, {
  NoPackageOptionAvailableCard
} from './PackageOptionCard';
import PackageDetailsModal from './PackageOptionCard/PackageDetailsModal';

const viewEvent = {
  customAttributes: {
    action: 'viewed',
    level: 'Options',
    section: 'Packages & Options'
  },
  eventName: 'New Cars'
};

const CardGrid = createComponent('div', {
  className: 'grid grid-cols-2 gap-md sm:grid-cols-3 md:grid-cols-4'
});

type PackageGroup = {
  kind: 'available' | 'selected' | 'unavailable';
  label: string;
  options: CustomizePackageOption[];
};

function PackagesSection() {
  const { customizeOptions, selectedOptions } = useContext(ParentModelContext);
  const { isPackageDetailsModalOpen, setPackageDetailsModalOpen } =
    useModalContext();
  const [selectedDetail, setSelectedDetail] =
    useState<CustomizePackageOption | null>(null);

  const handleDetailsClick = useCallback(
    (option: CustomizePackageOption) => {
      setSelectedDetail(option);
      setPackageDetailsModalOpen(true);
    },
    [setPackageDetailsModalOpen]
  );

  const handleDetailsClose = useCallback(() => {
    setPackageDetailsModalOpen(false);
    setSelectedDetail(null);
  }, [setPackageDetailsModalOpen]);

  const { make, packageOptions } = customizeOptions.data;

  const groups = useMemo<PackageGroup[]>(() => {
    const selectedSet = new Set(selectedOptions?.packageOptionCodes ?? []);

    const available: CustomizePackageOption[] = [];
    const selected: CustomizePackageOption[] = [];
    const unavailable: CustomizePackageOption[] = [];

    for (const option of packageOptions) {
      if (selectedSet.has(option.code)) {
        selected.push(option);
      } else if (option.count > 0) {
        available.push(option);
      } else {
        unavailable.push(option);
      }
    }

    const noneToEnd = (opts: CustomizePackageOption[]) =>
      opts.sort(
        (a, b) => Number(a.code === 'NONE') - Number(b.code === 'NONE')
      );

    return [
      {
        kind: 'selected' as const,
        label: 'You Selected',
        options: noneToEnd(selected)
      },
      {
        kind: 'available' as const,
        label: 'Still Available',
        options: noneToEnd(available)
      },
      {
        kind: 'unavailable' as const,
        label: 'Not Currently Available',
        options: noneToEnd(unavailable)
      }
    ].filter(group => group.options.length > 0);
  }, [packageOptions, selectedOptions?.packageOptionCodes]);

  if (!customizeOptions.isLoaded) return null;

  return (
    <TrackWrapper
      aria-labelledby={PACKAGES_SECTION_ID}
      className="flex flex-col gap-2xl px-2xl py-3xl"
      element="section"
      event={viewEvent}
      threshold={SECTION_VIEW_THRESHOLD}
      trackOnce
    >
      <h2
        className="scroll-mt-7xl t-heading-sm text-text-strong md:scroll-mt-4xl"
        id={PACKAGES_SECTION_ID}
      >
        Packages & Options
      </h2>
      {packageOptions.length > 0 ? (
        <LayoutGroup>
          <div className="flex flex-col gap-xl">
            <AnimatePresence initial={false}>
              {groups.map(({ kind, label, options }) => (
                <motion.div
                  animate={{ opacity: 1 }}
                  className="flex flex-col gap-2xl"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  key={label}
                  layout
                  transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                >
                  <p className="t-heading-xs text-text-strong">{label}</p>
                  <CardGrid>
                    {options.map(option => (
                      <motion.div
                        className="flex"
                        key={option.code}
                        layout
                        layoutId={option.code}
                        transition={{
                          type: 'spring',
                          damping: 30,
                          stiffness: 300
                        }}
                      >
                        <PackageOptionCard
                          isGreyedOut={option.count === 0}
                          make={make}
                          onDetailsClick={handleDetailsClick}
                          option={option}
                          selected={kind === 'selected'}
                        />
                      </motion.div>
                    ))}
                  </CardGrid>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </LayoutGroup>
      ) : (
        <CardGrid>
          <NoPackageOptionAvailableCard make={make} />
        </CardGrid>
      )}
      {selectedDetail && (
        <PackageDetailsModal
          description={selectedDetail.description}
          imageUrl={selectedDetail.imageUrl}
          isOpen={isPackageDetailsModalOpen}
          name={selectedDetail.name}
          onClose={handleDetailsClose}
        />
      )}
    </TrackWrapper>
  );
}

export default withErrorBoundary(PackagesSection, { section: 'packages' });
